<header>
  @if (envBarText) {
    <div class="text-center text-white text-xs bg-orange-500 h-3 leading-3">
      {{ envBarText }}
    </div>
  }
  <nav class="nav-row relative bg-[var(--scios-surface-1)] flex items-center gap-2 md:gap-3 py-3 border-b-2 border-[var(--scios-border)]">
    <!-- Left: Logo + entity context. On a phone the full logo becomes the star, so the club or
         player name has room (CC's phone audit, 04/10/2026). -->
    <div class="flex items-center gap-1 min-w-0 md:shrink-0">
      <a [routerLink]="getHomeLink()" aria-label="Home" class="nav-home flex items-center no-underline cursor-pointer shrink-0">
        <img src="/images/logo-icon.png" alt="Science of Sports" class="nav-logo-icon md:hidden">
        <img [src]="theme() === 'light' ? '/images/Logo-scios-bright.png' : '/images/Logo-scios-dark.png'" alt="Science of Sports" class="nav-wordmark hidden md:block h-10 object-contain">
      </a>
      <app-context-indicator class="min-w-0"></app-context-indicator>
    </div>

    <!-- Center: section tabs from tablet width up. They scroll sideways when they do not fit
         (768-1279px used to overflow both edges); on a phone they are the row under the bar. -->
    <div class="nav-tabs-wrap flex-1 min-w-0 hidden md:flex">
      <!-- A hub's sibling pages (Scout, Library) are a phone-only row: the laptop bar stays as it
           was (Release 5, 31/10/2026). -->
      @if (navCtx.hasContext() && !navCtx.phoneOnly()) {
        <nav class="nav-tabs" [class.nav-tabs--many]="navCtx.tabs().length > 6" aria-label="Section navigation">
          @for (tab of navCtx.tabs(); track tab.label) {
            <a [routerLink]="tab.link"
               [queryParams]="tab.queryParams ?? null"
               routerLinkActive="nav-tab--active"
               [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }"
               [class.nav-tab--active]="!!tab.key && tab.key === navCtx.activeCategory()"
               class="nav-tab" [appWhatsNewSpot]="tab.key ? 'nav.' + tab.link[0].slice(1) + '.' + tab.key : null">
              <span class="nav-tab__icon">
                @switch (tab.key) {
                  @case ('overview')    { <svg lucideLayoutDashboard [size]="17" aria-hidden="true"></svg> }
                  @case ('videos')      { <svg lucideVideo [size]="17" aria-hidden="true"></svg> }
                  @case ('stats')       { <svg lucideChartColumn [size]="17" aria-hidden="true"></svg> }
                  @case ('performance') { <svg lucideZap [size]="17" aria-hidden="true"></svg> }
                  @case ('events')      { <svg lucideClock [size]="17" aria-hidden="true"></svg> }
                  @case ('passing')     { <svg lucideWaypoints [size]="17" aria-hidden="true"></svg> }
                  @case ('matches')     { <svg lucideCalendar [size]="17" aria-hidden="true"></svg> }
                  @case ('teams')       { <svg lucideUsers [size]="17" aria-hidden="true"></svg> }
                  @case ('squad')       { <svg lucideUsers [size]="17" aria-hidden="true"></svg> }
                  @case ('players')     { <svg lucideUser [size]="17" aria-hidden="true"></svg> }
                  @case ('rankings')    { <svg lucideTrophy [size]="17" aria-hidden="true"></svg> }
                  @case ('report')      { <svg lucideClipboardList [size]="17" aria-hidden="true"></svg> }
                  @case ('goal-analysis') { <svg lucideGoal [size]="17" aria-hidden="true"></svg> }
                  @default              { <svg lucideLayoutDashboard [size]="17" aria-hidden="true"></svg> }
                }
              </span>
              <span class="nav-tab__label">{{ tab.label }}</span>
            </a>
          }
        </nav>
      }
    </div>

    <!-- Keeps the user menu hard right on mobile, where the tab strip is hidden -->
    <div class="flex-1 md:hidden"></div>

    <!-- LIVE: pulses red while matches are on air (CC, 01/10/2026). -->
    <app-live-nav-button class="shrink-0" />

    <!-- Right: the SCIOS launcher (grid) and the account card (avatar) - CC, 04/10/2026.
         Google's model: one button for "where can I go", one for "who am I". -->
    @if (currentUser(); as user) {
      <!-- Ask us on WhatsApp: the message names who asks and what is on screen (CC, 25/10/2026).
           Below 400px it is a row in the account panel instead, so the club or player name has
           room beside the logo (Release 5, 31/10/2026, SHELL-02). -->
      @if (!isNarrow()) {
        <app-whatsapp-help-button class="shrink-0" />
      }
      <!-- The bell: playlists shared with you, and later other messages (Release B2, CC 11/10/2026). -->
      <app-notification-bell class="shrink-0" />
      <button #appsBtn type="button"
              class="nav-icon-btn launcher-btn"
              [class.launcher-btn--open]="panel() === 'apps'"
              aria-label="SCIOS apps"
              aria-haspopup="dialog"
              aria-controls="nav-panel"
              [attr.aria-expanded]="panel() === 'apps'"
              (click)="togglePanel('apps')">
        <svg lucideGrip [size]="20"></svg>
      </button>

      <!-- Avatar only, as Google and Microsoft do (CC, 05/10/2026): the name lives in the card. -->
      <button #accountBtn type="button"
              class="account-btn"
              [class.account-btn--open]="panel() === 'account'"
              [attr.aria-label]="'Account: ' + getUserDisplayName(user)"
              [title]="getUserDisplayName(user) + (user.email ? '\n' + user.email : '')"
              aria-haspopup="dialog"
              aria-controls="nav-panel"
              [attr.aria-expanded]="panel() === 'account'"
              (click)="togglePanel('account')">
        <app-avatar [entity]="user" [size]="32"></app-avatar>
      </button>

      @if (panel(); as open) {
        <!-- Phone: the panel is a bottom sheet over a dimmed page. Desktop: a card under
             the bar, closed by a click anywhere else (document handler). -->
        @if (isPhone()) {
          <div class="nav-sheet-backdrop" (click)="closePanel()" aria-hidden="true"></div>
        }
        <div #panelEl id="nav-panel"
             class="nav-panel"
             [class.nav-panel--sheet]="isPhone()"
             role="dialog"
             [attr.aria-label]="open === 'apps' ? 'SCIOS apps' : 'Your account'"
             [style.transform]="dragY() > 0 ? 'translateY(' + dragY() + 'px)' : null">
          @if (isPhone()) {
            <!-- Grab area: drag down to close, like any phone sheet. The X is the
                 always-there touch way out. -->
            <div class="nav-sheet-grab"
                 (touchstart)="onGrabStart($event)"
                 (touchmove)="onGrabMove($event)"
                 (touchend)="onGrabEnd()"
                 (touchcancel)="onGrabEnd()">
              <span class="nav-sheet-handle" aria-hidden="true"></span>
              <div class="nav-sheet-head">
                <h2 class="nav-sheet-title">{{ open === 'apps' ? 'SCIOS apps' : 'Your account' }}</h2>
                <button type="button" class="nav-icon-btn" aria-label="Close" (click)="closePanel()">
                  <svg lucideX [size]="20"></svg>
                </button>
              </div>
            </div>
          }
          <div class="nav-panel-body" [class.nav-panel-body--account]="open === 'account'">
            @if (open === 'apps') {
              <app-launcher [sections]="launcherSections()" (picked)="closePanel($event.navigating)"></app-launcher>
            } @else {
              <app-account-panel [user]="user" [compact]="isPhone()" [whatsAppHref]="isNarrow() ? whatsAppHref() : null" (picked)="closePanel($event.navigating)" (signOut)="logout()"></app-account-panel>
            }
          </div>
        </div>
      }
    }
  </nav>

  <!-- Phone: the section tabs, always in sight under the bar (CC's phone audit, 04/10/2026 -- they
       used to hide behind a hamburger). Swipe for more; the edges fade while there is more to
       see, and the current tab is scrolled into view. The "back" chevron takes the place of the
       breadcrumb row, so the page gets one 44px row instead of two. -->
  @if (navCtx.hasContext()) {
    <div class="ptabs">
      @if (upCrumb(); as up) {
        <a class="ptabs__up" [routerLink]="up.link" [attr.aria-label]="'Back to ' + up.label">
          <svg lucideChevronLeft [size]="22" aria-hidden="true"></svg>
        </a>
      }
      <nav #phoneTabs
           class="ptabs__scroll"
           [class.ptabs__scroll--fade-start]="tabsFadeStart()"
           [class.ptabs__scroll--fade-end]="tabsFadeEnd()"
           (scroll)="updateTabFades()"
           aria-label="Section navigation">
        @for (tab of navCtx.tabs(); track tab.label) {
          <a [routerLink]="tab.link"
             [queryParams]="tab.queryParams ?? null"
             routerLinkActive="ptab--active"
             ariaCurrentWhenActive="page"
             [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }"
             [class.ptab--active]="!!tab.key && tab.key === navCtx.activeCategory()"
             class="ptab" [appWhatsNewSpot]="tab.key ? 'nav.' + tab.link[0].slice(1) + '.' + tab.key : null">{{ tab.label }}</a>
        }
      </nav>
      @if (navCtx.season(); as sn) {
        <select class="ptabs__season" [attr.aria-label]="sn.ariaLabel ?? 'Season'" (change)="onSeasonChange(sn, $event)">
          @for (s of sn.options; track s) {
            <option [value]="s" [selected]="sn.selected === s">{{ sn.format ? sn.format(s) : shortSeason(s) }}</option>
          }
        </select>
      }
    </div>
  }
</header>
